import { IItem } from '@/interfaces/IItem';
import {
  Label,
  PrintDensity,
  PrintDensityName,
  Spacing,
  Text,
  FontFamily,
  FontFamilyName,
  Alignment,
  AlignmentValue,
} from 'jszpl';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { OptionType } from '@/enums/OptionType';
import { IStickerSettings } from '@/interfaces/IStickerSettings';
import { getOptionQuantityPrefix, padNumber } from '@/utils';
import { usePrinting, usePrintingStore } from '@/composition/use-printing';
import { IOrder } from '@/interfaces/IOrder';
import { OrderType } from '@/enums/OrderEnums';
import {
  IZplPrinterSettings,
  injectZplOffsets,
  normalizeZplSettings,
  zplSettingsToSticker,
} from '@/lib/print/printer-settings';

export const defaultStickerSettings: IStickerSettings = {
  printDensity: '12dpmm',
  width: 55,
  height: 44,
  padding: {
    top: 20,
    right: 10,
    bottom: 20,
    left: 220,
  },
  fontSize: 'm',
};

export class LabelPrinter {
  static loadLabelNumber() {
    try {
      const labelNumber = localStorage.getItem('labelNumber');
      if (labelNumber) {
        return parseInt(labelNumber);
      }
    } catch (e) {}

    return 0;
  }

  static saveLabelNumber(value: number) {
    try {
      localStorage.setItem('labelNumber', value.toString());
    } catch (e) {}
  }

  static loadSettings() {
    const defaultSettings = defaultStickerSettings;

    // try to load from local storage
    try {
      let settings: any = localStorage.getItem('stickerSettings');
      if (settings) {
        settings = JSON.parse(settings);
      }

      return {
        ...defaultSettings,
        ...settings,
      };
    } catch (e) {
      console.error(e);
    }

    return JSON.parse(JSON.stringify(defaultSettings));
  }

  static async printItemLabel({
    item,
    template,
    order,
    orderItem,
    manualVariables,
    settings,
    zpl,
    printFn,
    itemMap,
    itemIndex,
  }: {
    item: IItem;
    template?: string;
    order?: IOrder;
    orderItem: IOrderItem;
    manualVariables: { key: string; value: string }[];
    settings?: IStickerSettings;
    /**
     * Explicit per-printer ZPL settings (V2 flows). Takes precedence over
     * `settings`; when absent, the legacy localStorage `stickerSettings`
     * are resolved at PRINT time (old sticker modal keeps working).
     */
    zpl?: IZplPrinterSettings;
    printFn?: (zpl: string) => Promise<any>;
    itemMap: { [key: number]: IItem };
    /** Absolute 1-based label position for $INDICE_ITEM (x of "x/total"). */
    itemIndex?: number;
  }) {
    template = template ?? item.labelTemplate;
    if (!template) throw new Error('Item has no label template');
    if (typeof item.recipe === 'undefined')
      throw new Error('Item recipe not loaded');

    if (zpl) settings = zplSettingsToSticker(normalizeZplSettings(zpl));
    else if (!settings) settings = this.loadSettings();
    else settings = { ...defaultStickerSettings, ...settings };

    // Only EXPLICIT per-printer settings emit the ^LH/^LS/^LT offset triplet
    // (which persists across jobs on real Zebra firmware, so once a printer
    // is on the new model every label must carry it to reset whatever a
    // previous calibration print left applied). Legacy-resolved labels emit
    // NO triplet — byte parity with pre-upgrade output, and printer-side
    // (front panel / ZSU) offset calibration keeps applying.
    const offsetSettings = zpl ? normalizeZplSettings(zpl) : null;

    const print =
      printFn ??
      (async (zpl: string) => {
        const printing = usePrinting();

        return await printing.printZpl(printing.stickerPrinter.value, zpl);
      });

    const label = new Label();
    label.printDensity = new PrintDensity(
      PrintDensityName[settings.printDensity]
    );
    label.width = settings.width;
    label.height = settings.height;
    label.padding = new Spacing(
      settings.padding.left,
      settings.padding.top,
      settings.padding.right,
      settings.padding.bottom
    );

    const labelNumber = this.loadLabelNumber() + 1;

    const parsedTemplate = this.parseItemLabelTemplate({
      template,
      item,
      order,
      orderItem,
      manualVariables,
      labelNumber,
      itemMap,
      itemIndex,
    });

    console.log(settings, parsedTemplate);

    const text = new Text();
    label.content.push(text);
    let fontFamily = FontFamilyName.F;
    switch (settings.fontSize) {
      case 'xs':
        fontFamily = FontFamilyName.B;
        break;
      case 's':
        fontFamily = FontFamilyName.D;
        break;
      case 'm':
        fontFamily = FontFamilyName.F;
        break;
      case 'l':
        fontFamily = FontFamilyName.E;
        break;
    }

    text.fontFamily = new FontFamily(fontFamily);
    text.text = LabelPrinter.replaceSpecialChars(parsedTemplate);
    text.verticalAlignment = new Alignment(AlignmentValue.Start);
    text.horizontalAlignment = new Alignment(AlignmentValue.Start);

    const zplDocument = offsetSettings
      ? injectZplOffsets(label.generateZPL(), offsetSettings)
      : label.generateZPL();

    await print(zplDocument);

    this.saveLabelNumber(labelNumber);
  }

  static parseItemLabelTemplate({
    template,
    item,
    order,
    orderItem,
    manualVariables,
    labelNumber,
    itemMap,
    itemIndex = 1,
  }: {
    template: string;
    item: IItem;
    order?: IOrder;
    orderItem: IOrderItem;
    manualVariables: { key: string; value: string }[];
    labelNumber: number;
    itemMap: { [key: number]: IItem };
    /** Absolute 1-based label position for $INDICE_ITEM (x of "x/total"). */
    itemIndex?: number;
  }) {
    let finalTemplate = template;

    // Replace all '$NOMBRE_PRODUCTO' with item.name
    finalTemplate = finalTemplate.replace(
      /\$NOMBRE_PRODUCTO/g,
      item.name.toUpperCase()
    );

    // Replace NUMERO_ETIQUETA
    finalTemplate = finalTemplate.replace(
      /\$NUMERO_ETIQUETA/g,
      labelNumber.toString()
    );

    // Replace INDICE_ITEM -> x/total (absolute order-wide label position,
    // provided by the caller via the sticker-indexing context).
    if (finalTemplate.includes('$INDICE_ITEM')) {
      if (!order) {
        finalTemplate = finalTemplate.replace(/\$INDICE_ITEM/g, '');
      } else {
        const itemCount = order.orderItems!.reduce((acc, oi) => {
          if (oi.canceledAt || !itemMap[oi.itemId]?.labelTemplate) return acc;
          return acc + oi.amount;
        }, 0);

        finalTemplate = finalTemplate.replace(
          /\$INDICE_ITEM/g,
          `${itemIndex}/${itemCount}`
        );
      }
    }

    // Replace COMENTARIO_PEDIDO
    if (order?.comment) {
      finalTemplate = finalTemplate.replace(
        /\$COMENTARIO_PEDIDO/g,
        order.comment
      );
    } else {
      finalTemplate = finalTemplate.replace(/\$COMENTARIO_PEDIDO/g, '');
    }

    // Replace HORARIO
    const now = new Date();
    finalTemplate = finalTemplate.replace(
      /\$HORARIO/g,
      `${padNumber(now.getHours(), 2)}:${padNumber(
        now.getMinutes(),
        2
      )}:${padNumber(now.getSeconds(), 2)}`
    );

    const optionsStrings: string[] = [];
    // Append options
    for (const option of orderItem.options) {
      const optionItem = option.optionItem!;
      const base = optionItem.option!;
      switch (base.type) {
        case OptionType.Choose:
        case OptionType.Add:
          optionsStrings.push(
            getOptionQuantityPrefix(base.type, option.val) + optionItem.title
          );
          break;
      }
    }

    const optionsFinalLines: string[] = [];

    if (optionsStrings.length) {
      optionsFinalLines.push(optionsStrings.join(', '));
    }

    if (orderItem.comment) {
      optionsFinalLines.push(orderItem.comment);
    }

    finalTemplate = finalTemplate.replace(
      /\$OPCIONES/g,
      optionsFinalLines.join('\n')
    );

    finalTemplate = finalTemplate.replace(/\$RECETA/g, item.recipe ?? '');

    if (manualVariables.length) {
      for (const variable of manualVariables) {
        finalTemplate = finalTemplate.replace(
          new RegExp(`\\$${variable.key}`, 'g'),
          variable.value
        );
      }
    }

    if (order) {
      finalTemplate = finalTemplate.replace(
        /\$NUMERO_PEDIDO/g,
        (order.humanId ?? order.id).toString()
      );

      finalTemplate = finalTemplate.replace(
        /\$NOMBRE_CLIENTE/g,
        order.userName ?? '-'
      );

      let tipoPedido;
      switch (order.type) {
        case OrderType.Seat:
          tipoPedido = 'Mesa';
          break;
        case OrderType.Away:
          tipoPedido = 'Para retirar';
          break;
        case OrderType.Delivery:
          tipoPedido = 'Delivery';
          break;
      }
      finalTemplate = finalTemplate.replace(/\$TIPO_PEDIDO/g, tipoPedido);
    }

    return finalTemplate;
  }

  static replaceSpecialChars(text: string) {
    return text
      .replace(/á/g, 'a')
      .replace(/Á/g, 'A')
      .replace(/é/g, 'e')
      .replace(/É/g, 'E')
      .replace(/í/g, 'i')
      .replace(/Í/g, 'I')
      .replace(/ó/g, 'o')
      .replace(/Ó/g, 'O')
      .replace(/ú/g, 'u')
      .replace(/Ú/g, 'U')
      .replace(/ñ/g, 'n')
      .replace(/Ñ/g, 'N');
  }
}
